import axios from "axios";
import { getAuthToken } from "./authToken";
import { FIDO_API_URL } from "../config";
import { getOrCreateDeviceUuid } from "./deviceIdentity";

const fidoApi = axios.create({
  baseURL: FIDO_API_URL,
});

fidoApi.interceptors.request.use(
  (config) => {
    // Prefer persisted session token (same source as main API) so credentials
    // listing always uses the authenticated person's JWT.
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/** Raw credential row from GET /v1/webauthn/credentials (snake_case). */
export interface BackendCredentialRaw {
  id: string;
  label: string;
  created_at: string;
  last_used_at?: string | null;
  transports?: string[] | null;
}

export interface BackendCredential {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt?: string | null;
  transports: string[];
}

export interface WebAuthnAttestationBody {
  id: string;
  rawId: string;
  type: string;
  response: {
    attestationObject: string;
    clientDataJSON: string;
    transports?: string[];
  };
  clientExtensionResults?: Record<string, unknown>;
}

export interface RegisterVerifyPayload {
  /** Fido2 AuthenticatorAttestationRawResponse — nested under attestation_response for label/transports. */
  attestation_response: WebAuthnAttestationBody;
  label: string;
  transports: string[];
}

export const buildRegisterVerifyPayload = (
  attestation: WebAuthnAttestationBody,
  label: string,
  transports: string[]
): RegisterVerifyPayload => ({
  attestation_response: attestation,
  label,
  transports,
});

export interface WebAuthnAssertionBody {
  id: string;
  rawId: string;
  type: string;
  response: {
    authenticatorData: string;
    clientDataJSON: string;
    signature: string;
    userHandle?: string;
  };
  clientExtensionResults?: Record<string, unknown>;
}

export interface StepUpOptionsPayload {
  transaction_id: string;
  amount?: number;
}

/** Token payload from POST /v1/webauthn/authenticate/verify. */
export interface WebAuthnTokenModel {
  access_token: string;
  token_type?: string;
  expires_in?: string | number;
  accepted_terms?: boolean;
  permissions?: string[];
  can_access_carteira?: boolean;
  can_access_console?: boolean;
  pending?: boolean;
  document_pending?: boolean;
  qualified?: boolean;
  role?: string;
}

export const mapBackendCredential = (
  cred: BackendCredentialRaw & {
    createdAt?: string;
    lastUsedAt?: string | null;
  }
): BackendCredential => ({
  id: String(cred.id),
  label: cred.label,
  createdAt: cred.created_at || cred.createdAt || new Date().toISOString(),
  lastUsedAt: cred.last_used_at ?? cred.lastUsedAt ?? null,
  transports: Array.isArray(cred.transports) ? cred.transports : ["usb"],
});

export const webauthnApi = {
  getRegisterOptions: () =>
    fidoApi.post<PublicKeyCredentialCreationOptions>(
      "/v1/oauth/webauthn/register/options"
    ),

  verifyRegistration: (data: RegisterVerifyPayload) =>
    fidoApi.post("/v1/oauth/webauthn/register/verify", data),

  getAuthOptions: (document: string) =>
    fidoApi.post<PublicKeyCredentialRequestOptions>(
      "/v1/oauth/webauthn/authenticate/options",
      { document }
    ),

  verifyAuthentication: (data: WebAuthnAssertionBody) =>
    fidoApi.post<WebAuthnTokenModel>(
      "/v1/oauth/webauthn/authenticate/verify",
      data,
      {
        headers: {
          "X-Device-Uuid": getOrCreateDeviceUuid(),
          "X-Device-Type": "browser",
        },
      }
    ),

  getStepUpOptions: (data: StepUpOptionsPayload) =>
    fidoApi.post<PublicKeyCredentialRequestOptions>(
      "/v1/oauth/webauthn/stepup/options",
      data
    ),

  verifyStepUp: (data: WebAuthnAssertionBody, transactionId: string) =>
    fidoApi.post("/v1/oauth/webauthn/stepup/verify", data, {
      params: { transactionId },
    }),

  getCredentials: async (): Promise<BackendCredential[]> => {
    const { data } = await fidoApi.get<BackendCredentialRaw[]>(
      "/v1/oauth/webauthn/credentials"
    );
    return data.map(mapBackendCredential);
  },

  removeCredential: (id: string) =>
    fidoApi.delete(
      `/v1/oauth/webauthn/credentials/${encodeURIComponent(id)}`
    ),
};
